import { useToast } from '@chakra-ui/react';
import { useAuthMutation } from 'features/common/apiUtils/queryHelper';
import { contextIdConfig, environments } from 'features/context-ids/config';
import { SitecoreContextType } from 'features/context-ids/types/types';
import { useQueryClient } from 'react-query';
import { EnvironmentDetails } from '../models/environment.model';

export const useRegenerateContextId = (
  deploymentInfo: EnvironmentDetails | undefined,
) => {
  const toast = useToast();

  const queryClient = useQueryClient();
  const { regenerate_contextid } = environments;
  const { url: regenerateURL, scope } = regenerate_contextid;
  const { queryKey: queryKeyContextIds } = contextIdConfig.get_contextids;

  const { mutateAsync, isLoading } = useAuthMutation(
    (axiosInstance) => async () =>
      axiosInstance?.post(regenerateURL(deploymentInfo?.id || '')),
    {
      onSuccess: async (response: { data: EnvironmentDetails }) => {
        if (!deploymentInfo) return;

        const contextIdMap: Record<string, string | null | undefined> = {
          [deploymentInfo.previewContextId as string]:
            response?.data?.previewContextId,
          [deploymentInfo.liveContextId as string]:
            response?.data?.liveContextId,
        };

        queryClient.setQueryData(
          queryKeyContextIds,
          (oldContextIds: SitecoreContextType[] = []) =>
            oldContextIds.map((context) => {
              const newContextId = contextIdMap[context.sitecoreContextId];

              if (!newContextId) {
                return context;
              }

              return {
                ...context,
                sitecoreContextId: newContextId,
              };
            }),
        );
        toast({
          title: 'Regeneration is successful',
          description: 'The Context ID has been successfully regenerated.',
          status: 'success',
          duration: 5000,
          isClosable: false,
        });
      },
      onError: () => {
        toast({
          title: 'Regeneration Error',
          description:
            'An unexpected error occurred while regenerating the Context ID. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: false,
        });
      },
    },
    scope,
  );

  return { mutateAsync, isLoading };
};
